import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { useAdminBasePath } from "@/lib/adminPath";
import { useOrganization } from "@/contexts/OrganizationContext";
import { 
  User,
  Package,
  TrendingUp,
  Calendar,
  DollarSign,
  Phone,
  Globe,
  ArrowLeft,
  ShoppingCart,
  Activity,
  BarChart3,
  PieChart,
  Clock,
  Target,
  ChevronRight,
  Settings,
  Trash2,
  AlertTriangle,
  MessageSquare,
  Send,
  Bell,
  Mail,
  Key,
  Shield,
  ExternalLink,
  Link2,
  Search,
  Loader2,
  CheckCircle2,
  Building2,
  Truck,
  MapPin,
  RefreshCw,
  Bot
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { QbSyncedMark } from "@/components/qb-synced-mark";
import { OrderSourceBadge } from "@/components/order-source-badge";
import { OrderSlip } from "@/components/order-slip";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const formatPhoneDisplay = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

import type { Account, OrderWithDetails, Product } from "@shared/schema";
import { PRODUCT_CATEGORIES } from "@shared/schema";
import { likelySameCustomer } from "@shared/accountNameMatch";
import { AddRequestToAccountDialog, hasRealPhone } from "@/components/add-request-to-account-dialog";
import { Textarea } from "@/components/ui/textarea";
import { reminderSettingsSchema, emailNotificationSchema } from "@shared/schema";
import { CategoryIcon } from "@/components/category-icon";
import { canViewFinancials } from "@/lib/adminRole";
import { AccountMenuCustomerType, useAccountCustomerType } from "@/components/account-customer-type";
import { MessageSaveHint, useMessageDraft } from "@/components/account-message-draft";
import { AccountAddressItem, AccountDeliveryZoneCard } from "@/components/account-address";
import { isAllowedByAccountMenu, isHiddenByType, typeHidesCategory } from "@/lib/customerTypes";
import { EMAIL_OVERRIDE_FIELDS, SMS_OVERRIDE_FIELDS, followsOrgDefaults, resetBody } from "@/lib/notificationOverrides";
import { accountThreadKey, consoleThreadPath, displayPhone, parseApiError, type AgentThreadSummary } from "@/lib/clydeThread";

type ValidDay = "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday";

// Account insights type
type AccountInsights = {
  totalOrders: number;
  totalItems: number;
  lifetimeValue: number;
  averageOrderSize: number;
  mostOrderedProducts: Array<{
    product: Product;
    totalQuantity: number;
    totalOrders: number;
  }>;
  ordersByMonth: Array<{
    month: string;
    orders: number;
    items: number;
  }>;
  ordersByStatus: {
    pending: number;
    reviewed: number;
    delivered: number;
    cancelled: number;
  };
  averageDaysBetweenOrders: number;
  lastOrderDate: string | null;
  firstOrderDate: string | null;
  activityTrend: 'increasing' | 'decreasing' | 'stable';
};

// The products query is off until the Menu tab is opened. Its empty default must
// be the SAME array on every render: it is a dependency of the effect that fills
// the menu form, and a fresh `[]` each time re-ran that effect on every render.
const NO_PRODUCTS: Product[] = [];

export default function AccountSummary() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const adminBase = useAdminBasePath();
  const { orgSlug } = useOrganization();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [fieldValue, setFieldValue] = useState<string>("");
  const [selectedOrder, setSelectedOrder] = useState<OrderWithDetails | null>(null);
  const [isOrderSlipOpen, setIsOrderSlipOpen] = useState(false);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState<number | null>(null);
  const [menuConfigOpen, setMenuConfigOpen] = useState<Record<number, boolean>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [canDeleteInfo, setCanDeleteInfo] = useState<{ canDelete: boolean; orderCount: number } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingDeliveryFee, setIsUpdatingDeliveryFee] = useState(false);
  // PIN management state
  const [isPinSetting, setIsPinSetting] = useState(false);
  // Test phone number for SMS
  const [testPhoneNumber, setTestPhoneNumber] = useState("2268680101");
  // Test email address for Email
  const [testEmailAddress, setTestEmailAddress] = useState("");
  // Notification form loading states
  const [smsFormLoading, setSmsFormLoading] = useState(false);
  const [emailFormLoading, setEmailFormLoading] = useState(false);
  // Authorized contacts state
  const [contactAddOpen, setContactAddOpen] = useState(false);
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactName, setNewContactName] = useState("");
  const [contactRemoveId, setContactRemoveId] = useState<number | null>(null);
  // DS-1: a one-time PIN setup code issued for a customer whose phone cannot
  // receive the texted one. The server returns it once; it lives only in this
  // dialog and is gone when the dialog closes.
  const [issuedSetupCode, setIssuedSetupCode] = useState<{ code: string; expiresAt: string; forName: string } | null>(null);
  const [issuingSetupCodeFor, setIssuingSetupCodeFor] = useState<"account" | number | null>(null);

  // QuickBooks linking state
  const [menuTabVisited, setMenuTabVisited] = useState(false);
  const [qbLinkOpen, setQbLinkOpen] = useState(false);
  const [qbSearchQuery, setQbSearchQuery] = useState("");
  const [qbDebouncedQuery, setQbDebouncedQuery] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // The Menu tab's form. It holds the menu and nothing else: the server acts on
  // every key an account PATCH carries (a name regenerates the custom URL, a phone
  // runs the duplicate-phone check), so the menu save must not carry them.
  const menuSchema = z.object({
    customMenu: z.object({
      categories: z.record(z.boolean()),
      products: z.record(z.boolean())
    }).optional()
  });

  type MenuForm = z.infer<typeof menuSchema>;

  const form = useForm<MenuForm>({
    resolver: zodResolver(menuSchema),
    defaultValues: {
      customMenu: {
        categories: {},
        products: {}
      }
    }
  });

  // SMS Reminder Form
  type SmsReminderForm = z.infer<typeof reminderSettingsSchema>;
  const smsForm = useForm<SmsReminderForm>({
    resolver: zodResolver(reminderSettingsSchema),
    defaultValues: {
      reminderEnabled: false,
      customReminderMessage: "",
      firstReminderDay: "monday",
      firstReminderTime: "08:00",
      secondReminderDay: "wednesday",
      secondReminderTime: "08:00",
      smsOrderReceipts: false
    }
  });

  // Email Notification Form
  type EmailNotificationForm = z.infer<typeof emailNotificationSchema>;
  const emailForm = useForm<EmailNotificationForm>({
    resolver: zodResolver(emailNotificationSchema),
    defaultValues: {
      emailNotificationEnabled: false,
      customEmailMessage: "",
      firstEmailDay: "monday",
      firstEmailTime: "08:00",
      secondEmailDay: "wednesday",
      secondEmailTime: "08:00",
      emailOrderReceipts: false
    }
  });

  // The two message boxes save when the admin leaves them (components/account-message-draft.tsx)
  const smsMessage = useMessageDraft({ form: smsForm, field: "customReminderMessage", route: "reminders" });
  const emailMessage = useMessageDraft({ form: emailForm, field: "customEmailMessage", route: "email-notifications" });

  // The browser's Back button leaves this account's page with no blur, so text still
  // in a message box is saved on the way out — for the account it was typed on
  const saveMessagesOnLeave = useRef(() => {});
  saveMessagesOnLeave.current = () => {
    smsMessage.save();
    emailMessage.save();
  };
  useEffect(() => () => saveMessagesOnLeave.current(), [id]);

  // Fetch account details
  const { data: account, isLoading: accountLoading } = useQuery<Account>({
    queryKey: ["/api/accounts", id],
    enabled: Boolean(id),
  });

  // The vendor's delivery charge, if it has one, and whether this account pays it
  // (`exempt` also counts its customer type and its group). No charge = no switch.
  const deliveryFeeKey = `/api/delivery-fee?accountId=${id ?? ''}`;
  const { data: deliveryFee } = useQuery<{ name: string; price: number | null; exempt: boolean } | null>({
    queryKey: [deliveryFeeKey],
    enabled: Boolean(id),
  });

  // Fetch account insights
  const { data: insights, isLoading: insightsLoading } = useQuery<AccountInsights>({
    queryKey: ["/api/accounts", id, "insights"],
    enabled: Boolean(id),
  });

  // Fetch QuickBooks lifetime value (served from cache with lazy background refresh).
  // Admin-only: staff must not see customer spend, and the server 403s them.
  const showFinancials = canViewFinancials();
  const { data: qbLifetimeValue } = useQuery<{
    available: boolean;
    totalRevenue?: number;
    invoiceCount?: number;
    averageInvoiceValue?: number;
    lastSyncedAt?: string;
    fromCache?: boolean;
    message?: string;
  }>({
    queryKey: ["/api/accounts", id, "quickbooks-lifetime-value"],
    enabled: Boolean(id) && showFinancials,
    retry: false,
    staleTime: 2 * 60 * 1000, // Frontend caches for 2 min to reduce requests
  });

  // Manual refresh mutation for QB lifetime value
  const refreshQbMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", `/api/accounts/${id}/quickbooks-refresh`)
        .then(res => {
          if (!res.ok) throw new Error('Refresh failed');
          return res.json();
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id, "quickbooks-lifetime-value"] });
      toast({ title: "QuickBooks data refreshed" });
    },
    onError: () => {
      toast({
        title: "Refresh failed",
        description: "Could not refresh QuickBooks data. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Fetch account orders
  const { data: orders = [], isLoading: ordersLoading } = useQuery<OrderWithDetails[]>({
    queryKey: ["/api/accounts", id, "orders"],
    enabled: Boolean(id),
  });

  // Fetch products for menu configuration (deferred until Menu tab is opened)
  // The account's customer type, when it has one (Menu tab panel)
  const accountType = useAccountCustomerType(account);

  const { data: products = NO_PRODUCTS, isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ["/api/products"],
    enabled: menuTabVisited,
  });

  // QuickBooks auth status
  const { data: qbAuthStatus } = useQuery<{ isAuthenticated: boolean; hasValidToken: boolean }>({
    queryKey: ["/api/quickbooks/auth-status"],
    retry: false,
    staleTime: 30000,
  });
  const qbConnected = qbAuthStatus?.isAuthenticated && qbAuthStatus?.hasValidToken;

  // All accounts (for QB conflict detection, and to list a pending group's companies)
  const isPendingRequest = account?.status === 'pending';
  // The account's live Clyde thread, if any — the route answers 404 when there is
  // none, which is an ordinary answer here, not an error.
  const { data: clydeThread } = useQuery<AgentThreadSummary | null>({
    queryKey: accountThreadKey(id),
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", `/api/accounts/${id}/agent-thread`);
        return (await res.json()) as AgentThreadSummary;
      } catch (err) {
        if (parseApiError(err).status === 404) return null;
        throw err;
      }
    },
    retry: false,
  });

  // Clyde's trust ladder for this account: the clean-YES streak, the "always ask"
  // pin and the org's threshold (server/services/clydeTrust.ts). Only with a thread.
  const clydeTrustKey = ["/api/accounts", id, "clyde-trust"];
  const { data: clydeTrust } = useQuery<{ streak: number; alwaysConfirm: boolean; autoPlaceAfter: number | null }>({
    queryKey: clydeTrustKey,
    queryFn: async () => (await apiRequest("GET", `/api/accounts/${id}/clyde-trust`)).json(),
    enabled: !!clydeThread,
    retry: false,
  });
  const setClydeAlwaysConfirm = useMutation({
    mutationFn: async (alwaysConfirm: boolean) =>
      (await apiRequest("PUT", `/api/accounts/${id}/clyde-trust`, { alwaysConfirm })).json(),
    onSuccess: (data) => queryClient.setQueryData(clydeTrustKey, data),
    onError: (err) => toast({ variant: "destructive", description: parseApiError(err).message || "Couldn't save that." }),
  });

  const { data: allAccounts = [] } = useQuery<Account[]>({
    queryKey: ["/api/accounts"],
    enabled: qbLinkOpen || isPendingRequest,
    staleTime: 30000,
  });
  const pendingCompanies = isPendingRequest && account?.isGroupParent
    ? allAccounts.filter(a => a.parentAccountId === account.id)
    : [];
  // An existing account that is probably the same customer (same name, or a
  // shared distinctive word) means the buyer couldn't sign in to it — approving
  // makes a second account and a second QuickBooks customer (Pitt, 2026-10-20).
  // The admin adds the request to that account instead.
  const addCandidates = isPendingRequest && account
    ? allAccounts.filter(a =>
        a.id !== account.id &&
        a.parentAccountId !== account.id &&
        a.status !== "pending" &&
        a.isActive !== false &&
        !a.isGroupParent &&
        !a.parentAccountId)
    : [];
  const sameCustomerMatches = isPendingRequest && account
    ? Array.from(
        new Map(
          (account.isGroupParent ? pendingCompanies.map(c => c.name) : [account.name])
            .flatMap(name => likelySameCustomer(name, allAccounts.filter(a =>
              a.id !== account.id && a.parentAccountId !== account.id && a.status !== "pending" && a.isActive !== false)))
            .map(m => [m.account.id, m] as const),
        ).values(),
      )
    : [];
  const [addToAccountOpen, setAddToAccountOpen] = useState(false);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);

  // BRIEF-account-request-flow: approve / decline a self-requested account.
  const [declineOpen, setDeclineOpen] = useState(false);
  const approveRequestMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/accounts/${id}/approve`);
      return response.json() as Promise<{
        account: Account;
        approvedAccountIds: number[];
        notifications: { sms: 'sent' | 'failed' | 'skipped'; email: 'sent' | 'failed' | 'skipped'; smsError?: string; emailError?: string };
      }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      const n = data.notifications;
      const sent = [n.sms === 'sent' ? 'text' : null, n.email === 'sent' ? 'email' : null].filter(Boolean);
      const failed = [n.sms === 'failed' ? 'text' : null, n.email === 'failed' ? 'email' : null].filter(Boolean);
      toast({
        title: "Account approved",
        description: failed.length > 0
          ? `Approved, but the ${failed.join(" and ")} could not be sent${sent.length ? ` (${sent.join(" and ")} sent)` : ""}. Let the customer know another way.`
          : sent.length > 0
            ? `The customer has been notified by ${sent.join(" and ")}.`
            : "No notification was sent — the account has no phone or email on file.",
        variant: failed.length > 0 ? "destructive" : undefined,
      });
    },
    onError: (error: any) => {
      toast({ title: "Could not approve", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });
  const declineRequestMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/accounts/${id}/decline`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      queryClient.removeQueries({ queryKey: ["/api/accounts", id] });
      toast({ title: "Request declined", description: "The pending account has been removed." });
      handleBack();
    },
    onError: (error: any) => {
      toast({ title: "Could not decline", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });

  // QuickBooks customer search
  const { data: qbSearchData, isLoading: qbSearchLoading } = useQuery<{
    matches: Array<{
      id: string;
      displayName: string;
      companyName?: string;
      email?: string;
      phone?: string;
      matchType: 'exact' | 'strong' | 'partial';
      score: number;
    }>;
  }>({
    queryKey: ["/api/quickbooks/search-customer", qbDebouncedQuery],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/quickbooks/search-customer?name=${encodeURIComponent(qbDebouncedQuery)}`);
      return response.json();
    },
    enabled: qbLinkOpen && qbDebouncedQuery.length >= 2,
    staleTime: 30000,
  });

  // Fetch QB customer details for the linked customer
  const { data: linkedQbCustomer, isLoading: linkedQbLoading } = useQuery<{
    id: string;
    displayName: string;
    companyName?: string;
    email?: string;
    phone?: string;
  } | null>({
    queryKey: ["/api/quickbooks/linked-customer", account?.quickbooksCustomerId, account?.name],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/quickbooks/search-customer?name=${encodeURIComponent(account!.name)}`);
      const data = await response.json();
      return data.matches?.find((m: any) => m.id === account!.quickbooksCustomerId) ?? null;
    },
    enabled: Boolean(account?.quickbooksCustomerId) && Boolean(qbConnected),
    retry: false,
    staleTime: 60000,
  });

  // Fetch categories for menu configuration (deferred until Menu tab is opened)
  const { data: categories = [] } = useQuery<any[]>({
    queryKey: ["/api/categories"],
    enabled: menuTabVisited,
  });

  // Fetch org-level order window config (for fulfillment override UI)
  const { data: windowConfig } = useQuery<any>({
    queryKey: ["/api/order-window-config"],
  });

  const orgDeliveryEnabled = windowConfig?.deliveryEnabled ?? true;
  const orgPickupEnabled = windowConfig?.pickupEnabled ?? false;

  // Fetch org-level notification defaults (for null-field inheritance display)
  const { data: notificationDefaults } = useQuery<any>({
    queryKey: ["/api/notification-defaults"],
  });
  const smsFollowsOrg = account ? followsOrgDefaults(account, SMS_OVERRIDE_FIELDS) : false;
  const emailFollowsOrg = account ? followsOrgDefaults(account, EMAIL_OVERRIDE_FIELDS) : false;

  // QB search debounce
  useEffect(() => {
    const timer = setTimeout(() => setQbDebouncedQuery(qbSearchQuery), 300);
    return () => clearTimeout(timer);
  }, [qbSearchQuery]);

  // Pre-populate QB search with account name when dialog opens
  useEffect(() => {
    if (qbLinkOpen && account) {
      setQbSearchQuery(account.name ?? "");
      setQbDebouncedQuery(account.name ?? "");
    } else if (!qbLinkOpen) {
      setQbSearchQuery("");
      setQbDebouncedQuery("");
    }
  }, [qbLinkOpen, account]);

  // Update form when account (and optionally products) load
  useEffect(() => {
    if (account) {
      // Initialize default menu config if none exists (all enabled by default)
      const defaultMenuConfig = {
        categories: Object.keys(PRODUCT_CATEGORIES).reduce((acc, catId) => {
          acc[catId] = true;
          return acc;
        }, {} as Record<string, boolean>),
        products: products.reduce((acc, product) => {
          acc[product.id.toString()] = true;
          return acc;
        }, {} as Record<string, boolean>)
      };

      // Use existing customMenu or default to all enabled
      const menuConfig = account.customMenu ? {
        categories: account.customMenu.categories || {},
        products: account.customMenu.products || {}
      } : defaultMenuConfig;

      form.reset({
        customMenu: menuConfig
      });
    }
  }, [account, products, form]);

  // The notification forms follow the account and the org defaults only — kept out
  // of the effect above so products arriving (the Menu tab's first open) cannot
  // reset a reminder message that is still being typed.
  useEffect(() => {
    if (account) {
      // Initialize SMS form — fall back to org defaults when account field is null.
      // A message typed and not yet saved stays in its box (refill): a save anywhere
      // else on the page refetches the account and lands here.
      const nd = notificationDefaults;
      smsForm.reset({
        reminderEnabled: account.reminderEnabled ?? nd?.smsEnabled ?? false,
        customReminderMessage: smsMessage.refill(account.id, account.customReminderMessage ?? nd?.smsTemplate ?? generateSmsDefaultMessage()),
        firstReminderDay: (account.firstReminderDay ?? nd?.smsFirstReminderDay ?? "monday") as ValidDay,
        firstReminderTime: account.firstReminderTime
          ? account.firstReminderTime.slice(0, 5)
          : (nd?.smsFirstReminderTime ?? "08:00").slice(0, 5),
        secondReminderDay: (account.secondReminderDay ?? nd?.smsSecondReminderDay ?? "wednesday") as ValidDay,
        secondReminderTime: account.secondReminderTime
          ? account.secondReminderTime.slice(0, 5)
          : (nd?.smsSecondReminderTime ?? "08:00").slice(0, 5),
        smsOrderReceipts: account.smsOrderReceipts ?? nd?.smsOrderReceipts ?? false,
      });

      // Initialize Email form — fall back to org defaults when account field is null
      emailForm.reset({
        emailNotificationEnabled: account.emailNotificationEnabled ?? nd?.emailEnabled ?? false,
        customEmailMessage: emailMessage.refill(account.id, account.customEmailMessage ?? nd?.emailTemplate ?? generateEmailDefaultMessage()),
        firstEmailDay: (account.firstEmailDay ?? nd?.emailFirstReminderDay ?? "monday") as ValidDay,
        firstEmailTime: account.firstEmailTime
          ? account.firstEmailTime.slice(0, 5)
          : (nd?.emailFirstReminderTime ?? "08:00").slice(0, 5),
        secondEmailDay: (account.secondEmailDay ?? nd?.emailSecondReminderDay ?? "wednesday") as ValidDay,
        secondEmailTime: account.secondEmailTime
          ? account.secondEmailTime.slice(0, 5)
          : (nd?.emailSecondReminderTime ?? "08:00").slice(0, 5),
        emailOrderReceipts: account.emailOrderReceipts ?? nd?.emailOrderReceipts ?? false,
      });
    }
  }, [account, notificationDefaults, smsForm, emailForm]);


  // Helper functions for menu configuration
  const getProductsByCategory = (categoryId: number) => {
    return products.filter(product => product.categoryId === categoryId);
  };

  const handleCategoryToggle = (categoryId: string, enabled: boolean) => {
    const currentMenu = form.getValues("customMenu") || { categories: {}, products: {} };
    const productsInCategory = getProductsByCategory(parseInt(categoryId));
    
    const updatedMenu = {
      ...currentMenu,
      categories: {
        ...currentMenu.categories,
        [categoryId]: enabled
      },
      products: {
        ...currentMenu.products,
        ...productsInCategory.reduce((acc, product) => {
          acc[product.id.toString()] = enabled;
          return acc;
        }, {} as Record<string, boolean>)
      }
    };
    
    form.setValue("customMenu", updatedMenu);
  };

  const handleProductToggle = (productId: string, enabled: boolean) => {
    const currentMenu = form.getValues("customMenu") || { categories: {}, products: {} };
    
    const updatedMenu = {
      ...currentMenu,
      products: {
        ...currentMenu.products,
        [productId]: enabled
      }
    };
    
    form.setValue("customMenu", updatedMenu);
  };

  const handleMenuSave = async () => {
    try {
      await apiRequest("PATCH", `/api/accounts/${id}`, { customMenu: form.getValues("customMenu") });
      toast({
        title: "Account updated",
        description: "Account has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
    } catch (error: any) {
      toast({
        title: "Failed to save account",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleFieldSave = async () => {
    if (!editingField || !account) return;
    
    try {
      const updateData: any = {};
      updateData[editingField] = fieldValue;
      
      await apiRequest("PATCH", `/api/accounts/${id}`, updateData);
      
      // Get proper field display name
      const getFieldDisplayName = (field: string) => {
        switch (field) {
          case 'emailAddress': return 'Email Address';
          case 'customUrl': return 'Custom URL';
          default: return field.charAt(0).toUpperCase() + field.slice(1);
        }
      };
      
      toast({
        title: "Account updated",
        description: `${getFieldDisplayName(editingField)} has been updated successfully.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      setEditingField(null);
      setFieldValue("");
    } catch (error: any) {
      toast({
        title: "Failed to save field",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleBack = () => {
    setLocation(`${adminBase}/accounts`);
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle order click to show order slip
  const handleOrderClick = (order: OrderWithDetails) => {
    setSelectedOrder(order);
    setIsOrderSlipOpen(true);
  };

  // Status update mutation
  const statusUpdateMutation = useMutation({
    mutationFn: async ({ orderId, status }: { orderId: number; status: string }) => {
      return apiRequest("PATCH", `/api/orders/${orderId}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id, "orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id, "insights"] });
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      setStatusPopoverOpen(null);
      toast({
        title: "Order updated",
        description: "Order status has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update order",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  // SMS Reminder Mutations
  const updateSmsReminderMutation = useMutation({
    mutationFn: ({ settings }: { settings: SmsReminderForm }) =>
      apiRequest('PATCH', `/api/accounts/${id}/reminders`, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      setSmsFormLoading(false);
      toast({
        title: "Success",
        description: "SMS reminder settings updated successfully"
      });
    },
    onError: (error: any) => {
      setSmsFormLoading(false);
      toast({
        title: "Error",
        description: error.message || "Failed to update SMS reminder settings",
        variant: "destructive"
      });
    }
  });

  const sendTestSmsMutation = useMutation({
    mutationFn: ({ message, testPhoneNumber }: { message?: string; testPhoneNumber?: string }) =>
      apiRequest('POST', '/api/reminders/test', { accountId: id, message, testPhoneNumber }),
    onSuccess: (response: any) => {
      toast({
        title: "Test SMS Sent",
        description: `Test message sent to ${response.recipient || 'the test number'}`
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send test SMS",
        variant: "destructive"
      });
    }
  });

  // Email Notification Mutations
  const updateEmailNotificationMutation = useMutation({
    mutationFn: ({ settings }: { settings: EmailNotificationForm }) =>
      apiRequest('PATCH', `/api/accounts/${id}/email-notifications`, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      setEmailFormLoading(false);
      toast({
        title: "Success",
        description: "Email notification settings updated successfully"
      });
    },
    onError: (error: any) => {
      setEmailFormLoading(false);
      toast({
        title: "Error",
        description: error.message || "Failed to update email notification settings",
        variant: "destructive"
      });
    }
  });

  const sendTestEmailMutation = useMutation({
    mutationFn: ({ message, testEmailAddress }: { message?: string; testEmailAddress?: string }) =>
      apiRequest('POST', '/api/email-notifications/test', { accountId: id, message, testEmailAddress }),
    onSuccess: (response: any) => {
      toast({
        title: "Test Email Sent",
        description: `Test message sent to ${response.recipient || 'the test email'}`
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send test email",
        variant: "destructive"
      });
    }
  });

  // Reset notification fields to null (inherit org defaults)
  const resetNotificationDefaultsMutation = useMutation({
    mutationFn: async (accountId: number) => {
      // A message box the admin has just left may still be saving: let that finish, so the reset lands last
      await Promise.allSettled([smsMessage.settled(), emailMessage.settled()]);
      return Promise.all([
        apiRequest('PATCH', `/api/accounts/${accountId}/reminders`, resetBody(SMS_OVERRIDE_FIELDS)),
        apiRequest('PATCH', `/api/accounts/${accountId}/email-notifications`, resetBody(EMAIL_OVERRIDE_FIELDS)),
      ]);
    },
    onSuccess: (_responses, accountId) => {
      // The reset wins over message text typed and not yet saved: both boxes go back to the org's message
      smsMessage.discard(accountId, notificationDefaults?.smsTemplate ?? generateSmsDefaultMessage());
      emailMessage.discard(accountId, notificationDefaults?.emailTemplate ?? generateEmailDefaultMessage());
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      toast({ title: "Success", description: "Notification settings reset to org defaults" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to reset notification settings", variant: "destructive" });
    }
  });

  // Fulfillment Override Mutation
  const updateFulfillmentOverrideMutation = useMutation({
    mutationFn: (data: { fulfillmentDelivery?: boolean | null; fulfillmentPickup?: boolean | null }) =>
      apiRequest('PATCH', `/api/accounts/${id}/fulfillment-overrides`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      toast({
        title: "Success",
        description: "Fulfillment options updated",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update fulfillment options",
        variant: "destructive",
      });
    },
  });

  // Authorized Contacts
  const { data: authorizedContacts } = useQuery<any[]>({
    queryKey: ['/api/accounts', id, 'authorized-contacts'],
    queryFn: () => apiRequest("GET", `/api/accounts/${id}/authorized-contacts`).then(r => r.json()),
    enabled: Boolean(id) && Boolean(account) && !account?.isGroupParent && !account?.parentAccountId,
  });

  const addContactMutation = useMutation({
    mutationFn: (data: { phone: string; name?: string }) =>
      apiRequest("POST", `/api/accounts/${id}/authorized-contacts`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/accounts', id, 'authorized-contacts'] });
      setContactAddOpen(false);
      setNewContactPhone("");
      setNewContactName("");
      toast({ title: "Contact added", description: "Authorized contact has been added." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to add contact", variant: "destructive" });
    },
  });

  const removeContactMutation = useMutation({
    mutationFn: (contactId: number) =>
      apiRequest("DELETE", `/api/accounts/${id}/authorized-contacts/${contactId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/accounts', id, 'authorized-contacts'] });
      setContactRemoveId(null);
      toast({ title: "Contact removed", description: "Authorized contact has been removed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to remove contact", variant: "destructive" });
    },
  });

  // QuickBooks link/unlink mutations
  const linkQBCustomerMutation = useMutation({
    mutationFn: ({ quickbooksCustomerId }: { quickbooksCustomerId: string }) =>
      apiRequest("POST", "/api/quickbooks/sync/link-customer", { accountId: Number(id), quickbooksCustomerId }),
    onSuccess: () => {
      toast({ title: "Linked to QuickBooks", description: "Account has been linked to QuickBooks customer." });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      setQbLinkOpen(false);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to link to QuickBooks customer.", variant: "destructive" });
    },
  });

  const unlinkQBCustomerMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/quickbooks/sync/unlink-customer", { accountId: Number(id) }),
    onSuccess: () => {
      toast({ title: "Unlinked from QuickBooks", description: "Account has been unlinked from QuickBooks." });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to unlink from QuickBooks.", variant: "destructive" });
    },
  });

  // Handle status update
  const handleStatusUpdate = (orderId: number, status: string) => {
    statusUpdateMutation.mutate({ orderId, status });
  };

  // Form submission handlers
  const handleSmsFormSubmit = (data: SmsReminderForm) => {
    setSmsFormLoading(true);
    updateSmsReminderMutation.mutate({ settings: data });
  };

  const handleEmailFormSubmit = (data: EmailNotificationForm) => {
    setEmailFormLoading(true);
    updateEmailNotificationMutation.mutate({ settings: data });
  };

  const handleSendTestSms = () => {
    const phoneNumber = testPhoneNumber.trim();
    if (!phoneNumber) {
      toast({
        title: "Phone number required",
        description: "Please enter a phone number to send the test SMS.",
        variant: "destructive"
      });
      return;
    }
    
    // Send the same message that appears in the Message Preview
    const customMessage = smsForm.getValues('customReminderMessage');
    const messageToSend = customMessage || notificationDefaults?.smsTemplate || generateSmsDefaultMessage();
    
    sendTestSmsMutation.mutate({
      message: messageToSend,
      testPhoneNumber: phoneNumber
    });
  };

  const handleSendTestEmail = () => {
    if (!testEmailAddress.trim()) {
      toast({
        title: "Email address required",
        description: "Please enter an email address to send the test email.",
        variant: "destructive"
      });
      return;
    }
    
    // Send the same message that's in the textarea
    const customMessage = emailForm.getValues('customEmailMessage');
    const messageToSend = customMessage || notificationDefaults?.emailTemplate || generateEmailDefaultMessage();
    
    sendTestEmailMutation.mutate({
      message: messageToSend,
      testEmailAddress: testEmailAddress
    });
  };

  // PIN Management - Reset PIN clears the PIN so customer sets a new one on next login
  const handleClearPin = async () => {
    setIsPinSetting(true);
    try {
      const response = await apiRequest("POST", `/api/accounts/${id}/clear-pin`);
      if (response.ok) {
        toast({
          title: "PIN Reset",
          description: "On their next login we'll text the customer a code, then they choose a new PIN. No mobile phone? Use \"Issue setup code\"."
        });
        queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
        queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      } else {
        const error = await response.json();
        toast({
          title: "Failed to reset PIN",
          description: error.message || "An error occurred",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reset PIN",
        variant: "destructive"
      });
    } finally {
      setIsPinSetting(false);
    }
  };

  // DS-1: for a customer whose phone cannot receive our text (landline, opted
  // out). Not a bypass — they still sign in with their phone number and enter
  // this code, with the same five tries.
  const handleIssueSetupCode = async (contact?: { id: number; name?: string | null; phone: string }) => {
    setIssuingSetupCodeFor(contact ? contact.id : "account");
    try {
      const response = await apiRequest("POST", `/api/accounts/${id}/pin-setup-code`, contact ? { contactId: contact.id } : {});
      const data = await response.json();
      setIssuedSetupCode({
        code: data.code,
        expiresAt: data.expiresAt,
        forName: contact ? (contact.name || formatPhoneDisplay(contact.phone)) : (account?.name ?? "this customer"),
      });
    } catch (error) {
      // apiRequest throws "<status>: <body>" on any non-2xx.
      let serverMessage: string | undefined;
      try {
        serverMessage = JSON.parse(String((error as Error)?.message ?? "").replace(/^\d+:\s*/, ''))?.message;
      } catch {
        // Not JSON — fall back to the generic message.
      }
      toast({
        title: "Couldn't issue a setup code",
        description: serverMessage || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setIssuingSetupCodeFor(null);
    }
  };

  // Helper functions for notification forms
  const generateSmsDefaultMessage = () => {
    // Use org-scoped URL pattern for multi-tenant SaaS
    const orgSlug = window.location.pathname.match(/^\/org\/([^/]+)/)?.[1] || '';
    const baseUrl = 'app.freshorder.io';
    const url = orgSlug && account?.customUrl 
      ? `${baseUrl}/org/${orgSlug}/order/${account.customUrl}` 
      : '[URL]';
    return `Hi ${account?.name || '[Account Name]'}, your ordering window is now open! Place your order at ${url}`;
  };

  const generateEmailDefaultMessage = () => {
    // Use org-scoped URL pattern for multi-tenant SaaS
    const orgSlug = window.location.pathname.match(/^\/org\/([^/]+)/)?.[1] || '';
    const baseUrl = 'app.freshorder.io';
    const url = orgSlug && account?.customUrl 
      ? `${baseUrl}/org/${orgSlug}/order/${account.customUrl}` 
      : '[URL]';
    return `Hello ${account?.name || '[Account Name]'},\n\nYour ordering window is now open! Please place your order at ${url}.\n\nThank you for your business.`;
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`1970-01-01T${timeString}:00`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        options.push({ value: timeString, label: displayTime });
      }
    }
    return options;
  };

  const generateDayOptions = () => {
    return [
      { value: "sunday", label: "Sunday" },
      { value: "monday", label: "Monday" },
      { value: "tuesday", label: "Tuesday" },
      { value: "wednesday", label: "Wednesday" },
      { value: "thursday", label: "Thursday" },
      { value: "friday", label: "Friday" },
      { value: "saturday", label: "Saturday" }
    ];
  };

  // Get status color for styling
  const getStatusColor = (status: string) => {
    switch (status) {
      case "reviewed": return "bg-orange-100 text-orange-800 hover:bg-orange-200";
      case "cancelled": return "bg-[var(--warning-light)] text-[var(--warning)] hover:bg-[var(--warning-light)]";
      default: return "bg-[var(--status-pending-bg)] text-cn-brand-blue hover:bg-[var(--status-pending-bg)]";
    }
  };

  if (accountLoading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Account Not Found</h2>
            <p className="text-gray-600 mb-6">The account you're looking for doesn't exist.</p>
            <Button onClick={handleBack} variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Accounts
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {isPendingRequest && (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 mt-6" data-testid="banner-account-request">
          <Card className="border-amber-300 bg-amber-50 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-amber-900">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Account request — awaiting your review
              </CardTitle>
              <CardDescription className="text-amber-900/80">
                This buyer asked for an account from the sign-in screen. They can't sign in or order until you approve.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-amber-700 shrink-0" />
                  <dt className="text-gray-600 w-20 shrink-0">Requested by</dt>
                  <dd className="font-medium text-gray-900 truncate">{account.requesterName || "—"}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-700 shrink-0" />
                  <dt className="text-gray-600 w-20 shrink-0">Requested</dt>
                  <dd className="font-medium text-gray-900">{account.requestedAt ? new Date(account.requestedAt).toLocaleString() : "—"}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-amber-700 shrink-0" />
                  <dt className="text-gray-600 w-20 shrink-0">Phone</dt>
                  <dd className="font-medium text-gray-900">
                    {account.phone ? <a href={`tel:${account.phone}`} className="hover:underline">{account.phone}</a> : "—"}
                  </dd>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-amber-700 shrink-0" />
                  <dt className="text-gray-600 w-20 shrink-0">Email</dt>
                  <dd className="font-medium text-gray-900 truncate">
                    {account.emailAddress ? <a href={`mailto:${account.emailAddress}`} className="hover:underline">{account.emailAddress}</a> : <span className="text-gray-500">none — SMS only</span>}
                  </dd>
                </div>
                <div className="flex items-start gap-2 sm:col-span-2">
                  <Building2 className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                  <dt className="text-gray-600 w-20 shrink-0">{account.isGroupParent ? "Companies" : "Company"}</dt>
                  <dd className="font-medium text-gray-900">
                    {account.isGroupParent
                      ? (pendingCompanies.length > 0 ? pendingCompanies.map(c => c.name).join(", ") : "Loading…")
                      : account.name}
                    {account.isGroupParent && (
                      <span className="block text-xs font-normal text-gray-600 mt-0.5">
                        One login for all of them — the buyer picks a company after entering their PIN.
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
              {sameCustomerMatches.length > 0 && (
                <div className="rounded-md border border-red-300 bg-white p-3 text-sm" data-testid="notice-request-same-name">
                  <p className="font-medium text-red-800">
                    {sameCustomerMatches.length === 1 ? "This may be a customer you already have:" : "This may be a customer you already have — one of:"}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {sameCustomerMatches.map(({ account: a, reason }) => (
                      <li key={a.id}>
                        <button
                          type="button"
                          className="text-red-800 underline hover:text-red-900"
                          onClick={() => setLocation(`${adminBase}/accounts/${a.id}`)}
                          data-testid={`link-same-name-account-${a.id}`}
                        >
                          {a.name}
                        </button>
                        <span className="text-gray-600">
                          {reason === "same-name" ? " · same name" : ""}
                          {hasRealPhone(a.phone) ? ` · ${a.phone}` : " · no real phone on file"}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-gray-700">
                    {account.isGroupParent
                      ? "If it's the same customer, decline this request and put their phone on the existing account. Approving makes a second account, and a second customer in QuickBooks."
                      : "If it's the same customer, use Add to an existing account. Approving makes a second account, and a second customer in QuickBooks."}
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                  onClick={() => setDeclineOpen(true)}
                  disabled={approveRequestMutation.isPending || declineRequestMutation.isPending}
                  data-testid="button-decline-request"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Decline
                </Button>
                {!account.isGroupParent && (
                  <Button
                    variant="outline"
                    className="border-amber-400 text-amber-900 hover:bg-amber-100"
                    onClick={() => setAddToAccountOpen(true)}
                    disabled={approveRequestMutation.isPending || declineRequestMutation.isPending}
                    data-testid="button-add-request-to-account"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Add to an existing account…
                  </Button>
                )}
                <Button
                  className="bg-[#3D8C40] hover:bg-green-700 text-white"
                  onClick={() => (sameCustomerMatches.length > 0 ? setApproveConfirmOpen(true) : approveRequestMutation.mutate())}
                  disabled={approveRequestMutation.isPending || declineRequestMutation.isPending}
                  data-testid="button-approve-request"
                >
                  {approveRequestMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Approve &amp; notify
                </Button>
              </div>
            </CardContent>
          </Card>

          {!account.isGroupParent && (
            <AddRequestToAccountDialog
              open={addToAccountOpen}
              onOpenChange={setAddToAccountOpen}
              request={account}
              candidates={addCandidates}
              suggestions={sameCustomerMatches.filter(m => addCandidates.some(c => c.id === m.account.id))}
              onAdded={(target) => setLocation(`${adminBase}/accounts/${target.id}`)}
            />
          )}

          <AlertDialog open={approveConfirmOpen} onOpenChange={setApproveConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>You may already have this customer</AlertDialogTitle>
                <AlertDialogDescription>
                  This looks like {sameCustomerMatches.map(m => m.account.name).join(", ")}. Approving makes a second account and, on its first invoice, a second customer in QuickBooks.
                  {!account.isGroupParent && " If it's the same customer, add this request to the existing account instead — their phone goes on it and nothing is duplicated."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2 sm:gap-0">
                <AlertDialogCancel data-testid="button-cancel-approve-anyway">Go back</AlertDialogCancel>
                {!account.isGroupParent && (
                  <Button
                    className="bg-[#3D8C40] hover:bg-green-700 text-white"
                    onClick={() => { setApproveConfirmOpen(false); setAddToAccountOpen(true); }}
                    data-testid="button-add-instead-of-approve"
                  >
                    Add to existing account
                  </Button>
                )}
                <AlertDialogAction
                  className="bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  onClick={() => approveRequestMutation.mutate()}
                  data-testid="button-approve-anyway"
                >
                  It's a different customer — approve
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={declineOpen} onOpenChange={setDeclineOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Decline this account request?</AlertDialogTitle>
                <AlertDialogDescription>
                  {account.isGroupParent
                    ? `This removes the request and all ${pendingCompanies.length || ""} companies under it.`
                    : "This removes the request."}{" "}
                  The buyer is not notified — reach out yourself if you want to explain. They can submit a new request later.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => declineRequestMutation.mutate()}
                  data-testid="button-confirm-decline"
                >
                  Decline request
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
      <Tabs defaultValue="overview" className="space-y-0" onValueChange={(v) => { if (v === "products") setMenuTabVisited(true); }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 mt-8">
          <TabsList className="h-12 p-0 bg-blue-100 rounded-t-lg rounded-b-none border-0 relative top-2 z-0 ml-1 sm:ml-2">
            <TabsTrigger 
              value="overview" 
              className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-cn-brand-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger 
              value="products" 
              className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-cn-brand-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
            >
              Menu
            </TabsTrigger>
            <TabsTrigger 
              value="trends" 
              className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-cn-brand-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
            >
              Trends
            </TabsTrigger>
            <TabsTrigger 
              value="orders" 
              className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-cn-brand-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
            >
              Orders
            </TabsTrigger>
            <TabsTrigger 
              value="notifications" 
              className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-cn-brand-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
            >
              Notifications
            </TabsTrigger>
          </TabsList>
        </div>
        
        <div className="max-w-7xl mx-auto px-3 sm:px-6 pb-3 sm:pb-6 relative z-20">

        <TabsContent value="overview" className="space-y-4">
            {/* Account Info Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <button 
                        className="font-medium text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline cursor-pointer text-left"
                        onClick={() => {
                          setEditingField('name');
                          setFieldValue(account.name);
                        }}
                      >
                        {account.name}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <button 
                        className="font-medium text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline cursor-pointer text-left"
                        onClick={() => {
                          setEditingField('phone');
                          setFieldValue(account.phone ?? '');
                        }}
                      >
                        {formatPhoneDisplay(account.phone ?? '')}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <button 
                        className="font-medium text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline cursor-pointer text-left"
                        onClick={() => {
                          setEditingField('emailAddress');
                          setFieldValue(account.emailAddress || "");
                        }}
                      >
                        {account.emailAddress || "Not set"}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Custom URL</p>
                      <div className="flex items-center gap-2">
                        <button 
                          className="font-medium text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline cursor-pointer text-left"
                          onClick={() => {
                            setEditingField('customUrl');
                            setFieldValue(account.customUrl || "");
                          }}
                        >
                          {account.customUrl || "Not set"}
                        </button>
                        {account.customUrl && orgSlug && (
                          <a
                            href={`/org/${orgSlug}/order/${account.customUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-cn-brand-blue transition-colors"
                            title="Open order page"
                            data-testid="link-open-order-page"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <AccountAddressItem account={account} />
                </div>
                {clydeThread && (
                  <div
                    className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-gray-100 pt-4 text-sm text-gray-700"
                    data-testid="text-clyde-thread-status"
                  >
                    <Bot className="h-4 w-4 text-cn-brand-green" />
                    <span>
                      Clyde thread: active since {new Date(clydeThread.createdAt).toLocaleDateString()}
                      {" · "}
                      {clydeThread.repAddress ? `rep ${displayPhone(clydeThread.repAddress)}` : "1:1, no rep"}
                      {clydeThread.channel === "whatsapp" ? " · WhatsApp" : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => setLocation(consoleThreadPath(adminBase, clydeThread.agentConversationId))}
                      className="text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline font-medium"
                      data-testid="link-open-clyde-thread"
                    >
                      Open
                    </button>
                    {clydeTrust && (
                      <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 pl-6 text-xs text-gray-600" data-testid="text-clyde-trust">
                        <span data-testid="text-clyde-streak">
                          {clydeTrust.streak === 1 ? "1 recap" : `${clydeTrust.streak} recaps`} confirmed in a row with no changes
                          {clydeTrust.autoPlaceAfter == null
                            ? " · skipping the recap is off for your org"
                            : clydeTrust.alwaysConfirm
                              ? " · always recapped (pinned)"
                              : clydeTrust.streak >= clydeTrust.autoPlaceAfter
                                ? " · clear orders now go straight in"
                                : ` · goes straight in after ${clydeTrust.autoPlaceAfter}`}
                        </span>
                        <label className="flex items-center gap-1.5">
                          <Switch
                            checked={clydeTrust.alwaysConfirm}
                            onCheckedChange={(v) => setClydeAlwaysConfirm.mutate(v)}
                            disabled={setClydeAlwaysConfirm.isPending}
                            data-testid="switch-clyde-always-confirm"
                          />
                          Always ask for confirmation
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Fulfillment Access */}
            <Card>
              <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Fulfillment Access
                </CardTitle>
                <CardDescription className="mt-1">
                  Control which fulfillment methods this account can use.
                  When set to default, the account inherits your org-wide fulfillment settings.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {(() => {
                  // Effective state: org-level AND account override combined
                  const effectiveDelivery = orgDeliveryEnabled && (account?.fulfillmentDelivery ?? true);
                  const effectivePickup = orgPickupEnabled && (account?.fulfillmentPickup ?? true);
                  // Lock a toggle ON if it's the only remaining enabled method
                  const deliveryIsLast = effectiveDelivery && !effectivePickup;
                  const pickupIsLast = effectivePickup && !effectiveDelivery;
                  return (
                    <>
                      {/* Delivery Toggle */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4 text-gray-600" />
                            <Label>Delivery</Label>
                            {!orgDeliveryEnabled && (
                              <span className="text-xs text-gray-400 ml-1">(not enabled org-wide)</span>
                            )}
                            {orgDeliveryEnabled && account?.fulfillmentDelivery === null && (
                              <span className="text-xs text-blue-500 ml-1">Using default</span>
                            )}
                          </div>
                          <Switch
                            checked={account?.fulfillmentDelivery ?? orgDeliveryEnabled}
                            disabled={!orgDeliveryEnabled || deliveryIsLast}
                            onCheckedChange={(checked) => {
                              const newValue = checked === orgDeliveryEnabled ? null : checked;
                              updateFulfillmentOverrideMutation.mutate({ fulfillmentDelivery: newValue });
                            }}
                          />
                        </div>
                        {deliveryIsLast && (
                          <p className="text-xs text-amber-600">At least one fulfillment method must be enabled.</p>
                        )}
                      </div>

                      {/* Pickup Toggle */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-gray-600" />
                            <Label>Pickup</Label>
                            {!orgPickupEnabled && (
                              <span className="text-xs text-gray-400 ml-1">(not enabled org-wide)</span>
                            )}
                            {orgPickupEnabled && account?.fulfillmentPickup === null && (
                              <span className="text-xs text-blue-500 ml-1">Using default</span>
                            )}
                          </div>
                          <Switch
                            checked={account?.fulfillmentPickup ?? orgPickupEnabled}
                            disabled={!orgPickupEnabled || pickupIsLast}
                            onCheckedChange={(checked) => {
                              const newValue = checked === orgPickupEnabled ? null : checked;
                              updateFulfillmentOverrideMutation.mutate({ fulfillmentPickup: newValue });
                            }}
                          />
                        </div>
                        {pickupIsLast && (
                          <p className="text-xs text-amber-600">At least one fulfillment method must be enabled.</p>
                        )}
                      </div>
                    </>
                  );
                })()}

                {/* Reset to defaults button — only show if either override is non-null */}
                {(account?.fulfillmentDelivery !== null || account?.fulfillmentPickup !== null) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-gray-500"
                    onClick={() => {
                      updateFulfillmentOverrideMutation.mutate({
                        fulfillmentDelivery: null,
                        fulfillmentPickup: null,
                      });
                    }}
                  >
                    Reset to org defaults
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Delivery Zone — renders only in an org that has delivery zones */}
            <AccountDeliveryZoneCard account={account} />

            {/* PIN Management Card */}
            <Card>
              <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-cn-brand-blue" />
                  Login Credentials
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <Shield className="h-8 w-8 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">PIN Status</p>
                      <p className="text-sm text-gray-600">
                        {(account as any).pin ? (
                          <span className="text-green-600 font-medium">PIN is set</span>
                        ) : (
                          <span className="text-amber-600">Customer will set PIN on first login</span>
                        )}
                      </p>
                    </div>
                  </div>
                  {(account as any).pin && (
                    <Button
                      variant="outline"
                      onClick={handleClearPin}
                      disabled={isPinSetting}
                      className="border-amber-500 text-amber-600 hover:bg-amber-50"
                      data-testid="button-reset-pin"
                    >
                      <Key className="h-4 w-4 mr-2" />
                      {isPinSetting ? 'Resetting...' : 'Reset PIN'}
                    </Button>
                  )}
                  {/* Same test the server applies: no PIN yet, or flagged for setup. */}
                  {(!(account as any).pin || account.requiresPinSetup) && !account.isGroupParent && !account.parentAccountId && account.phone && (
                    <Button
                      variant="outline"
                      onClick={() => handleIssueSetupCode()}
                      disabled={issuingSetupCodeFor !== null}
                      data-testid="button-issue-setup-code"
                    >
                      <Key className="h-4 w-4 mr-2" />
                      {issuingSetupCodeFor === "account" ? 'Issuing...' : 'Issue setup code'}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Customers set their own 4-digit PIN on first login, after entering a code we text to their phone. Use "Reset PIN" if a customer forgets their PIN - they'll confirm a new code and create a new PIN on their next visit. If their phone can't receive texts, "Issue setup code" gives you a one-time code to pass on instead.
                </p>
              </CardContent>
            </Card>

            {/* QuickBooks Linking */}
            {qbConnected && (
              <Card>
                <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                  <CardTitle className="flex items-center gap-2">
                    <Link2 className="h-5 w-5 text-cn-brand-blue" />
                    QuickBooks
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {account.quickbooksCustomerId ? (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        </div>
                        {linkedQbLoading ? (
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Loading customer details…
                          </div>
                        ) : linkedQbCustomer ? (
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{linkedQbCustomer.displayName}</p>
                            {linkedQbCustomer.companyName && linkedQbCustomer.companyName !== linkedQbCustomer.displayName && (
                              <p className="text-sm text-gray-500 flex items-center gap-1 truncate">
                                <Building2 className="h-3 w-3 flex-shrink-0" />
                                {linkedQbCustomer.companyName}
                              </p>
                            )}
                            {(linkedQbCustomer.email || linkedQbCustomer.phone) && (
                              <p className="text-xs text-gray-400 truncate">
                                {linkedQbCustomer.email ?? linkedQbCustomer.phone}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium text-gray-900">QuickBooks Customer #{account.quickbooksCustomerId}</p>
                            <p className="text-xs text-gray-400">Details unavailable</p>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unlinkQBCustomerMutation.mutate()}
                        disabled={unlinkQBCustomerMutation.isPending}
                        className="border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 sm:flex-shrink-0"
                      >
                        {unlinkQBCustomerMutation.isPending ? "Unlinking..." : "Unlink"}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <p className="text-sm text-gray-500">Not linked to a QuickBooks customer</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setQbLinkOpen(true)}
                        className="border-cn-brand-blue text-cn-brand-blue hover:bg-blue-50 sm:flex-shrink-0"
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        Link to QuickBooks
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Order Status Breakdown */}
            {insightsLoading ? (
              <Card>
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex justify-between">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-8"></div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : insights ? (
              <Card>
                <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                  <CardTitle>
                    Order Status Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <span>Pending</span>
                      </div>
                      <span className="font-medium">{insights.ordersByStatus.pending}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span>Reviewed</span>
                      </div>
                      <span className="font-medium">{insights.ordersByStatus.reviewed}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span>Sent to QB</span>
                      </div>
                      <span className="font-medium">{insights.ordersByStatus.delivered}</span>
                    </div>
                    {insights.ordersByStatus.cancelled > 0 && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <span>Cancelled</span>
                        </div>
                        <span className="font-medium">{insights.ordersByStatus.cancelled}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {/* Key Metrics */}
            {insightsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
                        <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : insights ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                    <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{insights.totalOrders}</div>
                    <p className="text-xs text-muted-foreground">
                      {insights.firstOrderDate ? `Since ${formatDate(insights.firstOrderDate)}` : "No orders yet"}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Items</CardTitle>
                    <Package className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{insights.totalItems}</div>
                    <p className="text-xs text-muted-foreground">
                      {insights.averageOrderSize.toFixed(1)} items per order
                    </p>
                  </CardContent>
                </Card>

                {showFinancials && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Lifetime Value
                      {qbLifetimeValue?.available && (
                        <Badge variant="secondary" className="ml-2 text-xs">QuickBooks</Badge>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => refreshQbMutation.mutate()}
                        disabled={refreshQbMutation.isPending}
                        className="p-1 rounded hover:bg-muted transition-colors"
                        title="Refresh QuickBooks data"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 text-muted-foreground ${refreshQbMutation.isPending ? 'animate-spin' : ''}`} />
                      </button>
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    {qbLifetimeValue?.available ? (
                      <>
                        <div className="text-2xl font-bold text-cn-green">
                          {formatCurrency(qbLifetimeValue.totalRevenue || 0)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {qbLifetimeValue.invoiceCount || 0} invoices • {formatCurrency(qbLifetimeValue.averageInvoiceValue || 0)} avg
                        </p>
                        {qbLifetimeValue.lastSyncedAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Synced {new Date(qbLifetimeValue.lastSyncedAt).toLocaleTimeString()}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-2xl font-bold text-muted-foreground">—</div>
                        <p className="text-xs text-muted-foreground">
                          QuickBooks data not available
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>
                )}

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Activity Trend</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      {insights.activityTrend === 'increasing' && (
                        <TrendingUp className="h-4 w-4 text-cn-green" />
                      )}
                      {insights.activityTrend === 'decreasing' && (
                        <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />
                      )}
                      {insights.activityTrend === 'stable' && (
                        <Activity className="h-4 w-4 text-blue-500" />
                      )}
                      <span className="text-2xl font-bold capitalize">{insights.activityTrend}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {insights.lastOrderDate ? `Last order: ${formatDate(insights.lastOrderDate)}` : "No recent orders"}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ) : null}

            {/* Order Frequency */}
            {insights && insights.averageDaysBetweenOrders > 0 && (
              <Card>
                <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                  <CardTitle>
                    Order Frequency
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-cn-brand-blue mb-2">
                      {Math.round(insights.averageDaysBetweenOrders)}
                    </div>
                    <p className="text-sm text-gray-600">Days between orders on average</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Account Status */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Settings className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium">Account Status</p>
                      <p className="text-xs text-gray-600">Enable or disable this customer account</p>
                    </div>
                  </div>
                  <Switch
                    checked={account.isActive}
                    disabled={isUpdatingStatus}
                    onCheckedChange={async (checked) => {
                      setIsUpdatingStatus(true);
                      try {
                        await apiRequest("PATCH", `/api/accounts/${account.id}/status`, { isActive: checked });
                        // Refetch account data
                        queryClient.invalidateQueries({ queryKey: [`/api/accounts/${account.id}`] });
                        toast({
                          title: "Account Status Updated",
                          description: `Account is now ${checked ? 'active' : 'inactive'}.`,
                        });
                      } catch (error: any) {
                        console.error('Failed to update status:', error);
                        toast({
                          title: "Failed to Update Status",
                          description: "Please try again later.",
                          variant: "destructive"
                        });
                      } finally {
                        setIsUpdatingStatus(false);
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Delivery charge — only for a vendor that has one */}
            {deliveryFee && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Truck className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">No delivery charge</p>
                        <p className="text-xs text-gray-600">
                          {account.deliveryFeeExempt
                            ? `${deliveryFee.name} is not added to this account's delivery orders${account.isGroupParent ? ', or to any account in its group' : ''}.`
                            : deliveryFee.exempt
                              ? `Already off through its customer type or its group.`
                              : `${deliveryFee.name}${deliveryFee.price != null ? ` ($${deliveryFee.price.toFixed(2)})` : ''} is added once to each delivery day's orders.`}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={account.deliveryFeeExempt}
                      disabled={isUpdatingDeliveryFee}
                      data-testid="switch-delivery-fee-exempt"
                      onCheckedChange={async (checked) => {
                        setIsUpdatingDeliveryFee(true);
                        try {
                          await apiRequest("PATCH", `/api/accounts/${account.id}`, { deliveryFeeExempt: checked });
                          queryClient.invalidateQueries({ queryKey: ["/api/accounts", id] });
                          queryClient.invalidateQueries({ queryKey: [deliveryFeeKey] });
                        } catch (error: any) {
                          console.error('Failed to update delivery charge:', error);
                          toast({
                            title: "Couldn't save",
                            description: "The delivery charge setting was not changed. Please try again.",
                            variant: "destructive"
                          });
                        } finally {
                          setIsUpdatingDeliveryFee(false);
                        }
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Authorized Contacts — standalone accounts only */}
            {account && !account.isGroupParent && !account.parentAccountId && (
              <Card>
                <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Authorized Contacts
                  </CardTitle>
                  <CardDescription className="mt-1">
                    These people can log in with their own phone number and PIN to place orders for this account.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  {/* Contact list */}
                  {authorizedContacts && authorizedContacts.length > 0 ? (
                    <div className="space-y-2">
                      {authorizedContacts.map((contact: any) => (
                        <div key={contact.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-3">
                            <Phone className="h-4 w-4 text-gray-400" />
                            <div>
                              {contact.name && <p className="text-sm font-medium">{contact.name}</p>}
                              <p className="text-sm text-gray-600">{formatPhoneDisplay(contact.phone)}</p>
                            </div>
                            {contact.requiresPinSetup && (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">PIN not set</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {contact.requiresPinSetup && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleIssueSetupCode(contact)}
                                disabled={issuingSetupCodeFor !== null}
                                data-testid={`button-issue-setup-code-contact-${contact.id}`}
                              >
                                {issuingSetupCodeFor === contact.id ? 'Issuing...' : 'Setup code'}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setContactRemoveId(contact.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No authorized contacts yet.</p>
                  )}

                  {/* Add contact form */}
                  {contactAddOpen ? (
                    <div className="border border-gray-200 rounded-md p-3 space-y-3 bg-gray-50">
                      <div className="space-y-1">
                        <Label className="text-xs">Phone number</Label>
                        <Input
                          placeholder="e.g. 416-555-1234"
                          value={newContactPhone}
                          onChange={e => setNewContactPhone(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Name (optional)</Label>
                        <Input
                          placeholder='e.g. "Chef Mike"'
                          value={newContactName}
                          onChange={e => setNewContactName(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={addContactMutation.isPending || !newContactPhone.trim()}
                          onClick={() => addContactMutation.mutate({ phone: newContactPhone.trim(), name: newContactName.trim() || undefined })}
                          className="bg-cn-brand-green hover:bg-cn-brand-green-hover text-white"
                        >
                          {addContactMutation.isPending ? "Saving..." : "Save"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setContactAddOpen(false); setNewContactPhone(""); setNewContactName(""); }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setContactAddOpen(true)}>
                      + Add Contact
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Delete Account */}
            <Card className="border-red-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Trash2 className="h-4 w-4 text-red-500" />
                    <div>
                      <p className="text-sm font-medium">Delete Account</p>
                      <p className="text-xs text-gray-600">Permanently remove this account</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={async () => {
                      // Check if account can be deleted
                      try {
                        // B12 (M-A6): use apiRequest (prepends /org/:slug) — a raw
                        // /api/... fetch fell through to the SPA catch-all and
                        // returned index.html, so response.json() failed silently.
                        const response = await apiRequest("GET", `/api/accounts/${id}/can-delete`);
                        if (!response.ok) throw new Error(`can-delete check failed: ${response.status}`);
                        const data = await response.json();
                        setCanDeleteInfo(data);
                        setDeleteDialogOpen(true);
                      } catch (error) {
                        toast({
                          title: "Error",
                          description: "Failed to check deletion status",
                          variant: "destructive"
                        });
                      }
                    }}
                    className="bg-white text-red-600 border border-red-600 hover:bg-red-600 hover:text-white"
                  >
                    Delete Account
                  </Button>
                </div>
              </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="products" className="space-y-4">
              {/* Custom Menu Configuration */}
            <Card className="mb-6">
              <CardHeader>
                <p className="text-sm text-gray-600">
                  Select which categories and products are available for this customer
                </p>
                {account && (
                  <AccountMenuCustomerType
                    account={account}
                    type={accountType}
                    products={products}
                    customMenu={form.watch("customMenu")}
                  />
                )}
              </CardHeader>
              <CardContent>
                
                <div className="max-h-64 overflow-y-auto border rounded-md p-3 space-y-2">
                  {productsLoading ? (
                    <div className="animate-pulse space-y-2 p-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-8 bg-gray-200 rounded"></div>
                      ))}
                    </div>
                  ) : products.length === 0 && categories.length === 0 ? (
                    <div className="text-sm text-gray-500">No products available</div>
                  ) : (
                    categories.map((category: any) => {
                      if (!category) return null;
                  
                      const categoryProducts = getProductsByCategory(category.id);
                      if (categoryProducts.length === 0) return null;
                      
                      const currentMenu = form.watch("customMenu") || { categories: {}, products: {} };
                      const isCategoryEnabled = currentMenu.categories?.[category.id.toString()] ?? true;
                      const isCollapsed = !menuConfigOpen[category.id];
                      // The type's rows mirror what it hides and are locked: the switches are
                      // this customer's own extras on top, never a way to change the type.
                      const categoryHiddenByType =
                        accountType != null &&
                        (typeHidesCategory(category.id, accountType) ||
                          categoryProducts.every((p: Product) => isHiddenByType(p, accountType)));

                      return (
                        <div key={category.id} className="border rounded-sm">
                          <div className="flex items-center justify-between p-2 bg-gray-50">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={categoryHiddenByType ? false : isCategoryEnabled}
                                disabled={categoryHiddenByType}
                                onCheckedChange={(checked) => 
                                  handleCategoryToggle(category.id.toString(), checked as boolean)
                                }
                              />
                              <span className={`text-sm font-medium inline-flex items-center gap-1 ${categoryHiddenByType ? "text-gray-400" : ""}`}>
                                <CategoryIcon icon={category.icon} size="sm" /> {category.name} ({categoryProducts.length} items)
                              </span>
                              {categoryHiddenByType ? (
                                <span className="text-xs text-blue-700">hidden by {accountType.name}</span>
                              ) : accountType && !isCategoryEnabled ? (
                                <span className="text-xs text-amber-700">hidden for this customer only</span>
                              ) : null}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => 
                                setMenuConfigOpen(prev => ({
                                  ...prev,
                                  [category.id]: !prev[category.id]
                                }))
                              }
                            >
                              <ChevronRight 
                                className={`h-3 w-3 transition-transform ${!isCollapsed ? 'rotate-90' : ''}`} 
                              />
                            </Button>
                          </div>
                          
                          {!isCollapsed && (
                            <Collapsible open={!isCollapsed}>
                              <CollapsibleContent className="p-2 space-y-1 bg-white">
                                {categoryProducts.map((product: any) => {
                                  const hiddenByType = accountType != null && isHiddenByType(product, accountType);
                                  const ownSwitch = currentMenu.products?.[product.id.toString()] ?? true;
                                  return (
                                    <div key={product.id} className="flex items-center space-x-2 pl-6 p-1 rounded bg-white">
                                      <Checkbox
                                        checked={hiddenByType ? false : ownSwitch}
                                        disabled={hiddenByType}
                                        onCheckedChange={(checked) => 
                                          handleProductToggle(product.id.toString(), checked as boolean)
                                        }
                                      />
                                      <span className={`text-xs ${hiddenByType ? "text-gray-400" : "text-gray-700"}`}>{product.name}</span>
                                      {hiddenByType ? (
                                        <span className="text-xs text-blue-700" data-testid={`text-hidden-by-type-${product.id}`}>
                                          hidden by {accountType.name}
                                        </span>
                                      ) : accountType && !ownSwitch ? (
                                        <span className="text-xs text-amber-700" data-testid={`text-hidden-for-account-${product.id}`}>
                                          hidden for this customer only
                                        </span>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </CollapsibleContent>
                            </Collapsible>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
            
                <div className="flex justify-end mt-4">
                  <Button
                    onClick={handleMenuSave}
                    className="bg-cn-brand-green text-white border border-cn-brand-green hover:bg-[hsl(140,27%,88%)] hover:text-cn-brand-green hover:border-[hsl(140,27%,88%)]"
                  >
                    Save Menu Configuration
                  </Button>
                </div>
              </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
              {insightsLoading ? (
              <Card>
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="flex justify-between">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : null}
              {!insightsLoading && insights && insights.ordersByMonth.length > 0 && (
              <Card>
                <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                  <CardTitle>
                    Monthly Order Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {insights.ordersByMonth.slice(-12).map((month) => (
                      <div key={month.month} className="flex items-center justify-between">
                        <span className="text-sm font-medium">{month.month}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-600">{month.orders} orders</span>
                          <span className="text-sm text-gray-600">{month.items} items</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {!insightsLoading && insights && insights.mostOrderedProducts.length > 0 && (
              <Card>
                <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                  <CardTitle>
                    Most Ordered Products
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {insights.mostOrderedProducts.slice(0, 10).map((item, index) => (
                      <div key={item.product.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-cn-brand-blue/10 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-cn-brand-blue">#{index + 1}</span>
                          </div>
                          <div>
                            <p className="font-medium">{item.product.name}</p>
                            <p className="text-sm text-gray-600">In {item.totalOrders} orders</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{item.totalQuantity} items</p>
                          <p className="text-sm text-gray-600">
                            {(item.totalQuantity / item.totalOrders).toFixed(1)} avg per order
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
        </TabsContent>

        <TabsContent value="orders" className="space-y-4">
              <Card>
              <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                <CardTitle>Order History</CardTitle>
              </CardHeader>
              <CardContent>
                {ordersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground">Loading orders...</span>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No orders found for this account.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader className="bg-gray-50">
                        <TableRow>
                          <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</TableHead>
                          <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Items</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOrderClick(order)}
                                className="text-cn-brand-blue hover:text-cn-brand-blue-hover p-0 h-auto font-mono text-sm"
                                data-testid={`button-order-${order.id}`}
                              >
                                ORD-{order.id.toString().padStart(3, '0')}
                              </Button>
                            </TableCell>
                            <TableCell>{formatDate(order.createdAt.toString())}</TableCell>
                            <TableCell>
                              <div className="flex flex-nowrap items-center gap-1.5">
                              <Popover open={statusPopoverOpen === order.id} onOpenChange={(open) => setStatusPopoverOpen(open ? order.id : null)}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`${getStatusColor(order.status)} p-1 h-auto text-xs cursor-pointer`}
                                    data-testid={`button-status-${order.id}`}
                                  >
                                    {order.status}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-48 p-2">
                                  <div className="space-y-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-full justify-start text-xs"
                                      onClick={() => handleStatusUpdate(order.id, "pending")}
                                      disabled={order.status === "pending"}
                                      data-testid={`button-mark-pending-${order.id}`}
                                    >
                                      Mark as Pending
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-full justify-start text-xs"
                                      onClick={() => handleStatusUpdate(order.id, "reviewed")}
                                      disabled={order.status === "reviewed"}
                                      data-testid={`button-mark-reviewed-${order.id}`}
                                    >
                                      Mark as Reviewed
                                    </Button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                              <QbSyncedMark syncedAt={(order as any).quickbooksSyncedAt} className="flex-shrink-0" />
                              <OrderSourceBadge source={(order as any).source} className="flex-shrink-0" />
                              </div>
                            </TableCell>
                            <TableCell>{order.totalItems}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          {/* SMS Notifications Form */}
          <div>
            <Card>
              <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-cn-brand-green" />
                    SMS Notifications
                    {smsFollowsOrg && (
                      <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Using org default</span>
                    )}
                  </CardTitle>
                  {/* One button resets both cards, so it shows while either has a setting of its own */}
                  {account && !(smsFollowsOrg && emailFollowsOrg) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-gray-500"
                      onClick={() => account && resetNotificationDefaultsMutation.mutate(account.id)}
                      disabled={resetNotificationDefaultsMutation.isPending}
                    >
                      Reset to org defaults
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* SMS Order Summary - comes first */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg gap-3">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor="sms-order-receipts" className="font-medium text-base sm:text-sm">SMS Order Summary</Label>
                    <p className="text-sm text-gray-600 mt-1">Send summaries via text when orders are submitted or updated</p>
                  </div>
                  <div className="flex justify-end sm:flex-shrink-0">
                    {/* Desktop: Circular Switch */}
                    <div className="hidden md:flex items-center">
                      <Switch
                        checked={smsForm.watch("smsOrderReceipts") ?? false}
                        onCheckedChange={(checked) => {
                          smsForm.setValue("smsOrderReceipts", checked);
                          
                          // Ensure time fields have valid defaults before auto-saving
                          if (!smsForm.watch("firstReminderTime")) {
                            smsForm.setValue("firstReminderTime", "08:00");
                          }
                          if (!smsForm.watch("secondReminderTime")) {
                            smsForm.setValue("secondReminderTime", "08:00");
                          }
                          
                          setSmsFormLoading(true);
                          updateSmsReminderMutation.mutate({ settings: smsForm.getValues() });
                        }}
                        data-testid="switch-sms-order-receipts"
                      />
                    </div>
                    {/* Mobile: ON/OFF Text Button */}
                    <Button
                      type="button"
                      variant={smsForm.watch("smsOrderReceipts") ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        smsForm.setValue("smsOrderReceipts", !smsForm.watch("smsOrderReceipts"));
                        
                        // Ensure time fields have valid defaults before auto-saving
                        if (!smsForm.watch("firstReminderTime")) {
                          smsForm.setValue("firstReminderTime", "08:00");
                        }
                        if (!smsForm.watch("secondReminderTime")) {
                          smsForm.setValue("secondReminderTime", "08:00");
                        }
                        
                        // Auto-save immediately
                        setSmsFormLoading(true);
                        updateSmsReminderMutation.mutate({ settings: smsForm.getValues() });
                      }}
                      className={`md:hidden min-w-[60px] h-8 text-xs font-medium ${
                        smsForm.watch("smsOrderReceipts") 
                          ? "bg-cn-brand-green text-white hover:bg-cn-brand-green/90" 
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      data-testid="button-sms-order-receipts"
                    >
                      {smsForm.watch("smsOrderReceipts") ? "ON" : "OFF"}
                    </Button>
                  </div>
                </div>

                {/* Enable SMS Reminders */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg gap-3">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor="sms-reminder-enabled" className="font-medium text-base sm:text-sm">Enable SMS Reminders</Label>
                    <p className="text-sm text-gray-600 mt-1">Send ordering window reminder notifications</p>
                  </div>
                  <div className="flex justify-end sm:flex-shrink-0">
                    {/* Desktop: Circular Switch */}
                    <div className="hidden md:flex items-center">
                      <Switch
                        checked={smsForm.watch("reminderEnabled") ?? false}
                        onCheckedChange={(checked) => {
                          smsForm.setValue("reminderEnabled", checked);
                          
                          // Ensure time fields have valid defaults before auto-saving
                          if (!smsForm.watch("firstReminderTime")) {
                            smsForm.setValue("firstReminderTime", "08:00");
                          }
                          if (!smsForm.watch("secondReminderTime")) {
                            smsForm.setValue("secondReminderTime", "08:00");
                          }
                          
                          setSmsFormLoading(true);
                          updateSmsReminderMutation.mutate({ settings: smsForm.getValues() });
                        }}
                        data-testid="switch-sms-reminder-enabled"
                      />
                    </div>
                    {/* Mobile: ON/OFF Text Button */}
                    <Button
                      type="button"
                      variant={smsForm.watch("reminderEnabled") ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        const newValue = !smsForm.watch("reminderEnabled");
                        smsForm.setValue("reminderEnabled", newValue);
                        
                        // Ensure time fields have valid defaults before auto-saving
                        if (!smsForm.watch("firstReminderTime")) {
                          smsForm.setValue("firstReminderTime", "08:00");
                        }
                        if (!smsForm.watch("secondReminderTime")) {
                          smsForm.setValue("secondReminderTime", "08:00");
                        }
                        
                        // Auto-save immediately
                        setSmsFormLoading(true);
                        updateSmsReminderMutation.mutate({ settings: smsForm.getValues() });
                      }}
                      className={`md:hidden min-w-[60px] h-8 text-xs font-medium ${
                        smsForm.watch("reminderEnabled") 
                          ? "bg-cn-brand-green text-white hover:bg-cn-brand-green/90" 
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      data-testid="button-sms-reminder-enabled"
                    >
                      {smsForm.watch("reminderEnabled") ? "ON" : "OFF"}
                    </Button>
                  </div>
                </div>

                {smsForm.watch("reminderEnabled") && (
                  <div className="ml-2 sm:ml-6 pl-3 sm:pl-4 border-l-2 border-gray-200 space-y-6">

                    {/* Custom Message */}
                    <div className="space-y-3">
                      <Label htmlFor="sms-custom-message" className="font-medium">Custom Reminder Message</Label>
                      <Textarea
                        id="sms-custom-message"
                        {...smsForm.register("customReminderMessage", { onBlur: smsMessage.save })}
                        placeholder="Enter your custom reminder message"
                        className="resize-none bg-[rgb(243,244,246)]"
                        rows={3}
                        data-testid="textarea-sms-custom-message"
                      />
                      <MessageSaveHint draft={smsMessage} />
                      <p className="text-sm text-muted-foreground">Available template variables: <code className="text-xs bg-gray-100 px-1 rounded">[account]</code>, <code className="text-xs bg-gray-100 px-1 rounded">[active order deadline]</code>, <code className="text-xs bg-gray-100 px-1 rounded">[delivery day]</code>, <code className="text-xs bg-gray-100 px-1 rounded">[org-url]</code></p>
                      {smsForm.formState.errors.customReminderMessage && (
                        <p className="text-sm text-red-600">{smsForm.formState.errors.customReminderMessage.message}</p>
                      )}
                    </div>

                    {/* SMS Reminder Schedule */}
                    <div className="space-y-4">
                      <Label className="font-medium text-sm">Reminder Schedule</Label>
                      <div className="space-y-4">
                        {/* First Reminder */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700">First Reminder</Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Select
                              value={smsForm.watch("firstReminderDay") ?? undefined}
                              onValueChange={(value) => {
                                smsForm.setValue("firstReminderDay", value as ValidDay);
                                setSmsFormLoading(true);
                                updateSmsReminderMutation.mutate({ settings: smsForm.getValues() });
                              }}
                            >
                              <SelectTrigger 
                                className="bg-[rgb(243,244,246)] text-sm"
                                data-testid="select-first-reminder-day"
                              >
                                <SelectValue placeholder="Select day" />
                              </SelectTrigger>
                              <SelectContent>
                                {generateDayOptions().map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={smsForm.watch("firstReminderTime") ?? undefined}
                              onValueChange={(value) => {
                                smsForm.setValue("firstReminderTime", value);
                                setSmsFormLoading(true);
                                updateSmsReminderMutation.mutate({ settings: smsForm.getValues() });
                              }}
                            >
                              <SelectTrigger 
                                className="bg-[rgb(243,244,246)] text-sm"
                                data-testid="select-first-reminder-time"
                              >
                                <SelectValue placeholder="Select time" />
                              </SelectTrigger>
                              <SelectContent>
                                {generateTimeOptions().map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {(smsForm.formState.errors.firstReminderDay || smsForm.formState.errors.firstReminderTime) && (
                            <p className="text-xs text-red-600">
                              {smsForm.formState.errors.firstReminderDay?.message || smsForm.formState.errors.firstReminderTime?.message}
                            </p>
                          )}
                        </div>

                        {/* Second Reminder */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700">Second Reminder</Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Select
                              value={smsForm.watch("secondReminderDay") ?? undefined}
                              onValueChange={(value) => {
                                smsForm.setValue("secondReminderDay", value as ValidDay);
                                setSmsFormLoading(true);
                                updateSmsReminderMutation.mutate({ settings: smsForm.getValues() });
                              }}
                            >
                              <SelectTrigger 
                                className="bg-[rgb(243,244,246)] text-sm"
                                data-testid="select-second-reminder-day"
                              >
                                <SelectValue placeholder="Select day" />
                              </SelectTrigger>
                              <SelectContent>
                                {generateDayOptions().map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={smsForm.watch("secondReminderTime") ?? undefined}
                              onValueChange={(value) => {
                                smsForm.setValue("secondReminderTime", value);
                                setSmsFormLoading(true);
                                updateSmsReminderMutation.mutate({ settings: smsForm.getValues() });
                              }}
                            >
                              <SelectTrigger 
                                className="bg-[rgb(243,244,246)] text-sm"
                                data-testid="select-second-reminder-time"
                              >
                                <SelectValue placeholder="Select time" />
                              </SelectTrigger>
                              <SelectContent>
                                {generateTimeOptions().map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {(smsForm.formState.errors.secondReminderDay || smsForm.formState.errors.secondReminderTime) && (
                            <p className="text-xs text-red-600">
                              {smsForm.formState.errors.secondReminderDay?.message || smsForm.formState.errors.secondReminderTime?.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Test SMS */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 border rounded-lg gap-3">
                      <div className="flex-1">
                        <p className="font-medium text-sm mb-2">Test SMS</p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <span className="text-xs text-gray-600">Send test message to:</span>
                          <Input
                            type="tel"
                            value={testPhoneNumber}
                            onChange={(e) => setTestPhoneNumber(e.target.value)}
                            placeholder="Enter phone number"
                            className="h-8 text-xs px-2 w-full sm:w-40 bg-blue-100"
                            data-testid="input-test-phone-number"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSendTestSms}
                        disabled={sendTestSmsMutation.isPending}
                        className="w-full sm:w-auto bg-blue-100 text-cn-brand-blue border-blue-200 hover:bg-cn-brand-blue hover:text-white hover:border-cn-brand-blue"
                        data-testid="button-send-test-sms"
                      >
                        <Send className="h-3 w-3 mr-1" />
                        {sendTestSmsMutation.isPending ? 'Sending...' : 'Send Test'}
                      </Button>
                    </div>
                  </div>
                )}

              </CardContent>
            </Card>
          </div>

          {/* Email Notifications Form */}
          <div>
            <Card>
              <CardHeader className="border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-cn-brand-green" />
                    Email Notifications
                    {emailFollowsOrg && (
                      <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Using org default</span>
                    )}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* Email Order Summary - comes first */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg gap-3">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor="email-order-receipts" className="font-medium text-base sm:text-sm">Email Order Summary</Label>
                    <p className="text-sm text-gray-600 mt-1">Send order summaries when orders are submitted or updated</p>
                  </div>
                  <div className="flex justify-end sm:flex-shrink-0">
                    {/* Desktop: Circular Switch */}
                    <div className="hidden md:flex items-center">
                      <Switch
                        checked={emailForm.watch("emailOrderReceipts") ?? false}
                        onCheckedChange={(checked) => {
                          emailForm.setValue("emailOrderReceipts", checked);
                          
                          // Ensure time fields have valid defaults before auto-saving
                          if (!emailForm.watch("firstEmailTime")) {
                            emailForm.setValue("firstEmailTime", "08:00");
                          }
                          if (!emailForm.watch("secondEmailTime")) {
                            emailForm.setValue("secondEmailTime", "08:00");
                          }
                          
                          setEmailFormLoading(true);
                          updateEmailNotificationMutation.mutate({ settings: emailForm.getValues() });
                        }}
                        data-testid="switch-email-order-receipts"
                      />
                    </div>
                    {/* Mobile: ON/OFF Text Button */}
                    <Button
                      type="button"
                      variant={emailForm.watch("emailOrderReceipts") ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        emailForm.setValue("emailOrderReceipts", !emailForm.watch("emailOrderReceipts"));
                        
                        // Ensure time fields have valid defaults before auto-saving
                        if (!emailForm.watch("firstEmailTime")) {
                          emailForm.setValue("firstEmailTime", "08:00");
                        }
                        if (!emailForm.watch("secondEmailTime")) {
                          emailForm.setValue("secondEmailTime", "08:00");
                        }
                        
                        // Auto-save immediately
                        setEmailFormLoading(true);
                        updateEmailNotificationMutation.mutate({ settings: emailForm.getValues() });
                      }}
                      className={`md:hidden min-w-[60px] h-8 text-xs font-medium ${
                        emailForm.watch("emailOrderReceipts") 
                          ? "bg-cn-brand-green text-white hover:bg-cn-brand-green/90" 
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      data-testid="button-email-order-receipts"
                    >
                      {emailForm.watch("emailOrderReceipts") ? "ON" : "OFF"}
                    </Button>
                  </div>
                </div>

                {/* Enable Email Notifications */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg gap-3">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor="email-notification-enabled" className="font-medium text-base sm:text-sm">Enable Email Reminders</Label>
                    <p className="text-sm text-gray-600 mt-1">Send ordering window reminder emails</p>
                  </div>
                  <div className="flex justify-end sm:flex-shrink-0">
                    {/* Desktop: Circular Switch */}
                    <div className="hidden md:flex items-center">
                      <Switch
                        checked={emailForm.watch("emailNotificationEnabled") ?? false}
                        onCheckedChange={(checked) => {
                          emailForm.setValue("emailNotificationEnabled", checked);
                          setEmailFormLoading(true);
                          updateEmailNotificationMutation.mutate({ settings: emailForm.getValues() });
                        }}
                        data-testid="switch-email-notification-enabled"
                      />
                    </div>
                    {/* Mobile: ON/OFF Text Button */}
                    <Button
                      type="button"
                      variant={emailForm.watch("emailNotificationEnabled") ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        emailForm.setValue("emailNotificationEnabled", !emailForm.watch("emailNotificationEnabled"));
                        // Auto-save immediately
                        setEmailFormLoading(true);
                        updateEmailNotificationMutation.mutate({ settings: emailForm.getValues() });
                      }}
                      className={`md:hidden min-w-[60px] h-8 text-xs font-medium ${
                        emailForm.watch("emailNotificationEnabled") 
                          ? "bg-cn-brand-green text-white hover:bg-cn-brand-green/90" 
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      data-testid="button-email-notification-enabled"
                    >
                      {emailForm.watch("emailNotificationEnabled") ? "ON" : "OFF"}
                    </Button>
                  </div>
                </div>
                
                {emailForm.watch("emailNotificationEnabled") && (
                  <div className="ml-2 sm:ml-6 pl-3 sm:pl-4 border-l-2 border-gray-200 space-y-6">


                    {/* Custom Email Message */}
                    <div className="space-y-3">
                      <Label htmlFor="email-custom-message" className="font-medium">Custom Email Message</Label>
                      <Textarea
                        id="email-custom-message"
                        {...emailForm.register("customEmailMessage", { onBlur: emailMessage.save })}
                        placeholder="Enter your custom email message"
                        className="resize-none bg-[rgb(243,244,246)]"
                        rows={6}
                        data-testid="textarea-email-custom-message"
                      />
                      <MessageSaveHint draft={emailMessage} />
                      <p className="text-sm text-muted-foreground">Available template variables: <code className="text-xs bg-gray-100 px-1 rounded">[account]</code>, <code className="text-xs bg-gray-100 px-1 rounded">[active order deadline]</code>, <code className="text-xs bg-gray-100 px-1 rounded">[delivery day]</code>, <code className="text-xs bg-gray-100 px-1 rounded">[org-url]</code></p>
                      {emailForm.formState.errors.customEmailMessage && (
                        <p className="text-sm text-red-600">{emailForm.formState.errors.customEmailMessage.message}</p>
                      )}
                    </div>

                    {/* Email Notification Schedule */}
                    <div className="space-y-4">
                      <Label className="font-medium text-sm">Email Schedule</Label>
                      <div className="space-y-4">
                        {/* First Email */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700">First Email</Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Select
                              value={emailForm.watch("firstEmailDay") ?? undefined}
                              onValueChange={(value) => {
                                emailForm.setValue("firstEmailDay", value as ValidDay);
                                setEmailFormLoading(true);
                                updateEmailNotificationMutation.mutate({ settings: emailForm.getValues() });
                              }}
                            >
                              <SelectTrigger 
                                className="bg-[rgb(243,244,246)] text-sm"
                                data-testid="select-first-email-day"
                              >
                                <SelectValue placeholder="Select day" />
                              </SelectTrigger>
                              <SelectContent>
                                {generateDayOptions().map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={emailForm.watch("firstEmailTime") ?? undefined}
                              onValueChange={(value) => {
                                emailForm.setValue("firstEmailTime", value);
                                setEmailFormLoading(true);
                                updateEmailNotificationMutation.mutate({ settings: emailForm.getValues() });
                              }}
                            >
                              <SelectTrigger 
                                className="bg-[rgb(243,244,246)] text-sm"
                                data-testid="select-first-email-time"
                              >
                                <SelectValue placeholder="Select time" />
                              </SelectTrigger>
                              <SelectContent>
                                {generateTimeOptions().map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {(emailForm.formState.errors.firstEmailDay || emailForm.formState.errors.firstEmailTime) && (
                            <p className="text-xs text-red-600">
                              {emailForm.formState.errors.firstEmailDay?.message || emailForm.formState.errors.firstEmailTime?.message}
                            </p>
                          )}
                        </div>

                        {/* Second Email */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700">Second Email</Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Select
                              value={emailForm.watch("secondEmailDay") ?? undefined}
                              onValueChange={(value) => {
                                emailForm.setValue("secondEmailDay", value as ValidDay);
                                setEmailFormLoading(true);
                                updateEmailNotificationMutation.mutate({ settings: emailForm.getValues() });
                              }}
                            >
                              <SelectTrigger 
                                className="bg-[rgb(243,244,246)] text-sm"
                                data-testid="select-second-email-day"
                              >
                                <SelectValue placeholder="Select day" />
                              </SelectTrigger>
                              <SelectContent>
                                {generateDayOptions().map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={emailForm.watch("secondEmailTime") ?? undefined}
                              onValueChange={(value) => {
                                emailForm.setValue("secondEmailTime", value);
                                setEmailFormLoading(true);
                                updateEmailNotificationMutation.mutate({ settings: emailForm.getValues() });
                              }}
                            >
                              <SelectTrigger 
                                className="bg-[rgb(243,244,246)] text-sm"
                                data-testid="select-second-email-time"
                              >
                                <SelectValue placeholder="Select time" />
                              </SelectTrigger>
                              <SelectContent>
                                {generateTimeOptions().map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {(emailForm.formState.errors.secondEmailDay || emailForm.formState.errors.secondEmailTime) && (
                            <p className="text-xs text-red-600">
                              {emailForm.formState.errors.secondEmailDay?.message || emailForm.formState.errors.secondEmailTime?.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Test Email */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 border rounded-lg gap-3">
                      <div className="flex-1">
                        <p className="font-medium text-sm mb-2">Test Email</p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <span className="text-xs text-gray-600">Send test message to:</span>
                          <Input
                            type="email"
                            value={testEmailAddress}
                            onChange={(e) => setTestEmailAddress(e.target.value)}
                            placeholder="Enter email"
                            className="h-8 text-xs px-2 w-full sm:w-48 bg-blue-100"
                            data-testid="input-test-email-address"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSendTestEmail}
                        disabled={sendTestEmailMutation.isPending}
                        className="w-full sm:w-auto bg-blue-100 text-cn-brand-blue border-blue-200 hover:bg-cn-brand-blue hover:text-white hover:border-cn-brand-blue"
                        data-testid="button-send-test-email"
                      >
                        <Send className="h-3 w-3 mr-1" />
                        {sendTestEmailMutation.isPending ? 'Sending...' : 'Send Test'}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Warning for missing email address */}
                {(emailForm.watch("emailOrderReceipts") || emailForm.watch("emailNotificationEnabled")) && !account?.emailAddress && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                      <p className="text-sm text-orange-800 font-medium">Email address required</p>
                    </div>
                    <p className="text-sm text-orange-700 mt-1">
                      This account needs an email address to receive email notifications. Please add one in the Account Details tab.
                    </p>
                  </div>
                )}

              </CardContent>
            </Card>
          </div>
        </TabsContent>
        </div>
      </Tabs>

      {/* Order Slip Modal */}
      <OrderSlip
        order={selectedOrder}
        isOpen={isOrderSlipOpen}
        onClose={() => setIsOrderSlipOpen(false)}
      />

      {/* Edit Field Dialog */}
      <Dialog open={!!editingField} onOpenChange={(open) => !open && setEditingField(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Edit {editingField === 'customUrl' ? 'Custom URL' : 
                   editingField === 'emailAddress' ? 'Email Address' : 
                   editingField ? editingField.charAt(0).toUpperCase() + editingField.slice(1) : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="fieldValue">
                {editingField === 'name' && 'Account Name'}
                {editingField === 'phone' && 'Phone Number'}
                {editingField === 'emailAddress' && 'Email Address'}
                {editingField === 'customUrl' && 'Custom URL'}
              </Label>
              <Input
                id="fieldValue"
                type={editingField === 'emailAddress' ? 'email' : 'text'}
                value={fieldValue}
                onChange={(e) => setFieldValue(e.target.value)}
                placeholder={
                  editingField === 'name' ? 'Enter account name' :
                  editingField === 'phone' ? 'Enter phone number' :
                  editingField === 'emailAddress' ? 'Enter email address' :
                  'Enter custom URL'
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleFieldSave();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingField(null);
                  setFieldValue("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleFieldSave}
                className="bg-cn-brand-green hover:bg-cn-brand-green-hover"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QuickBooks Link Dialog */}
      <Dialog open={qbLinkOpen} onOpenChange={setQbLinkOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-cn-brand-blue" />
              Link to QuickBooks Customer
            </DialogTitle>
            <DialogDescription>
              Search for a QuickBooks customer to link to <strong>{account?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={qbSearchQuery}
                onChange={(e) => setQbSearchQuery(e.target.value)}
                placeholder="Search by customer name..."
                className="pl-9"
                autoFocus
              />
            </div>

            <div className="space-y-2 max-h-80 overflow-y-auto">
              {qbSearchLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Searching...
                </div>
              ) : qbDebouncedQuery.length < 2 ? (
                <div className="text-center py-8 text-sm text-gray-400">
                  Type at least 2 characters to search
                </div>
              ) : !qbSearchData?.matches?.length ? (
                <div className="text-center py-8 text-sm text-gray-500">
                  No QuickBooks customers found for "{qbDebouncedQuery}"
                </div>
              ) : (
                qbSearchData.matches.map((match) => {
                  const conflictAccount = allAccounts.find(
                    (a) => a.quickbooksCustomerId === match.id && a.id !== account?.id
                  );
                  return (
                    <div
                      key={match.id}
                      className={`flex items-center justify-between p-3 border rounded-lg gap-3 ${
                        conflictAccount ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{match.displayName}</span>
                          <Badge className={
                            match.matchType === 'exact' ? 'bg-green-100 text-green-800 border-0' :
                            match.matchType === 'strong' ? 'bg-blue-100 text-blue-800 border-0' :
                            'bg-yellow-100 text-yellow-800 border-0'
                          }>
                            {match.matchType}
                          </Badge>
                        </div>
                        {match.companyName && (
                          <p className="text-xs text-gray-500 mt-0.5">{match.companyName}</p>
                        )}
                        {conflictAccount && (
                          <div className="flex items-center gap-1 mt-1">
                            <AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0" />
                            <p className="text-xs text-amber-700">Already linked to {conflictAccount.name}</p>
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        disabled={!!conflictAccount || linkQBCustomerMutation.isPending}
                        onClick={() => linkQBCustomerMutation.mutate({ quickbooksCustomerId: match.id })}
                        className={conflictAccount ? '' : 'bg-cn-brand-green hover:bg-cn-brand-green-hover text-white flex-shrink-0'}
                      >
                        Link
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* DS-1: one-time PIN setup code — shown once */}
      <Dialog open={issuedSetupCode !== null} onOpenChange={open => { if (!open) setIssuedSetupCode(null); }}>
        <DialogContent className="sm:max-w-[420px]" data-testid="dialog-setup-code">
          <DialogHeader>
            <DialogTitle>Setup code for {issuedSetupCode?.forName}</DialogTitle>
            <DialogDescription>
              Give this code to the customer. They enter their phone number on the sign-in screen, then this code, then choose their PIN.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-gray-50 border border-gray-200 py-5 text-center">
            <p
              className="text-4xl font-semibold tracking-[0.3em] text-gray-900 tabular-nums pl-[0.3em]"
              data-testid="text-setup-code"
            >
              {issuedSetupCode?.code}
            </p>
          </div>
          <ul className="text-sm text-gray-600 space-y-1 list-disc pl-5">
            <li>This code is shown once. Closing this window hides it for good — issue a new one if you need to.</li>
            <li>
              It works one time, allows 5 tries, and expires{" "}
              {issuedSetupCode
                ? new Date(issuedSetupCode.expiresAt).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })
                : ""}
              .
            </li>
            <li>Only pass it to someone you know is this customer.</li>
          </ul>
          <DialogFooter>
            <Button onClick={() => setIssuedSetupCode(null)} data-testid="button-setup-code-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Authorized Contact Confirmation Dialog */}
      <Dialog open={contactRemoveId !== null} onOpenChange={open => { if (!open) setContactRemoveId(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Remove Authorized Contact</DialogTitle>
            <DialogDescription>
              {contactRemoveId !== null && authorizedContacts && (() => {
                const contact = authorizedContacts.find((c: any) => c.id === contactRemoveId);
                return contact
                  ? `Remove ${contact.name ? `${contact.name}'s` : formatPhoneDisplay(contact.phone) + "'s"} access to this account?`
                  : "Remove this contact's access to this account?";
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setContactRemoveId(null)}>Cancel</Button>
            <Button
              className="bg-white text-red-600 border border-red-600 hover:bg-red-600 hover:text-white"
              disabled={removeContactMutation.isPending}
              onClick={() => contactRemoveId !== null && removeContactMutation.mutate(contactRemoveId)}
            >
              {removeContactMutation.isPending ? "Removing..." : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {canDeleteInfo?.canDelete ? (
                <>
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  Confirm Account Deletion
                </>
              ) : (
                <>
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Cannot Delete Account
                </>
              )}
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-4">
              {canDeleteInfo?.canDelete ? (
                <>
                  <p className="font-medium text-gray-900">
                    Are you sure you want to permanently delete the account "{account?.name}"?
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-sm text-yellow-800">
                      <strong>Warning:</strong> This action is permanent and cannot be undone.
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    This account has no order history and can be safely deleted.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-gray-900">
                    The account "{account?.name}" cannot be deleted.
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">
                      This account has <strong>{canDeleteInfo?.orderCount} order{canDeleteInfo?.orderCount === 1 ? '' : 's'}</strong> in the system.
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    Only accounts with zero order history can be deleted. To preserve data integrity, accounts with existing orders must be kept in the system. You can mark this account as inactive instead.
                  </p>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {canDeleteInfo?.canDelete ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setDeleteDialogOpen(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    setIsDeleting(true);
                    try {
                      const response = await apiRequest("DELETE", `/api/accounts/${id}`);
                      toast({
                        title: "Account Deleted",
                        description: `${account?.name} has been permanently deleted.`
                      });
                      // Redirect to accounts list
                      setLocation(`${adminBase}/accounts`);
                    } catch (error: any) {
                      toast({
                        title: "Error",
                        description: error.message || "Failed to delete account",
                        variant: "destructive"
                      });
                    } finally {
                      setIsDeleting(false);
                      setDeleteDialogOpen(false);
                    }
                  }}
                  disabled={isDeleting}
                  className="bg-white text-red-600 border border-red-600 hover:bg-red-600 hover:text-white"
                >
                  {isDeleting ? "Deleting..." : "Delete Account"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                className="bg-cn-brand-green hover:bg-cn-brand-green-hover"
              >
                Understood
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </div>
  );
}